/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './Notes.css';

const Icons = {
    Plus: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    Star: ({ filled }) => <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 9 22 9 16.5 13.5 18.8 21 12 16.8 5.2 21 7.5 13.5 2 9 9 9 12 2"></polygon></svg>,
    Check: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Trash: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    Calendar: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    Book: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5z"></path><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"></path></svg>,
    Megaphone: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l15-6v14L3 13z"></path><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path></svg>,
    Note: () => <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"></path><path d="M8 8h8"></path><path d="M8 12h8"></path><path d="M8 16h5"></path></svg>
};

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const pad = (value) => String(value).padStart(2, '0');
// Value for <input type="datetime-local">, in the teacher's own time zone.
const toLocalInput = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

// One-tap due dates; reminders default to 07:30, before the school day starts.
const quickDueDates = () => {
    const today = startOfDay(new Date());
    const at = (days) => { const date = new Date(today.getTime() + days * DAY_MS); date.setHours(7, 30); return date; };
    const untilWeekday = (weekday) => ((weekday - today.getDay() + 7) % 7) || 7;
    return [
        { label: 'Tomorrow', value: toLocalInput(at(1)) },
        { label: 'Friday', value: toLocalInput(at(untilWeekday(5))) },
        { label: 'Next Monday', value: toLocalInput(at(untilWeekday(1))) },
        { label: 'In 2 weeks', value: toLocalInput(at(14)) },
    ];
};

const formatDue = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });
    const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
    return hasTime ? `${day}, ${date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}` : day;
};

// Which section of the list a reminder belongs in.
const bucketFor = (note) => {
    if (note.is_done === 1) return 'done';
    if (!note.due_date) return 'someday';
    const due = new Date(note.due_date);
    if (due < new Date()) return 'overdue';
    const days = Math.floor((startOfDay(due) - startOfDay(new Date())) / DAY_MS);
    if (days === 0) return 'today';
    if (days <= 7) return 'week';
    return 'later';
};

const BUCKETS = [
    { id: 'overdue', title: 'Overdue' },
    { id: 'today', title: 'Today' },
    { id: 'week', title: 'Next 7 days' },
    { id: 'later', title: 'Later' },
    { id: 'someday', title: 'No due date' },
    { id: 'done', title: 'Completed' },
];

const VIEWS = [
    { id: 'open', label: 'To do' },
    { id: 'overdue', label: 'Overdue' },
    { id: 'important', label: 'Important' },
    { id: 'done', label: 'Completed' },
    { id: 'all', label: 'All' },
];

export default function Notes() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notes, setNotes] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [view, setView] = useState('open');
    const [sortBy, setSortBy] = useState('due_date');
    const [error, setError] = useState('');
    const [canCreateSchoolNotes, setCanCreateSchoolNotes] = useState(false);
    const [composeScope, setComposeScope] = useState('subject');
    const [selectedSubjectId, setSelectedSubjectId] = useState('');
    const [noteText, setNoteText] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [isImportant, setIsImportant] = useState(false);

    const emitNotesUpdated = (detail = {}) => {
        try {
            window.dispatchEvent(new CustomEvent('notesUpdated', { detail }));
        } catch {}
    };

    const loadNotes = useCallback(async () => {
        setError('');
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_all_notes.php`, {
                params: { sort_by: 'created_at', sort_order: 'DESC' }
            });

            if (!response.data.success) {
                setError(response.data.error || 'Failed to load notes');
                setNotes([]);
                setSubjects([]);
                setCanCreateSchoolNotes(false);
                return;
            }

            const fetchedSubjects = Array.isArray(response.data.subjects) ? response.data.subjects : [];
            setNotes(response.data.notes || []);
            setSubjects(fetchedSubjects);
            setCanCreateSchoolNotes(Boolean(response.data.permissions?.can_create_school_notes));
        } catch {
            setError('Failed to load notes');
            setNotes([]);
            setSubjects([]);
            setCanCreateSchoolNotes(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    useEffect(() => {
        const hasSelectedSubject = subjects.some((subject) => String(subject.id) === String(selectedSubjectId));

        if (subjects.length > 0 && !hasSelectedSubject) {
            setSelectedSubjectId(String(subjects[0].id));
        }

        if (subjects.length === 0 && canCreateSchoolNotes && composeScope === 'subject') {
            setComposeScope('school');
        }
    }, [subjects, selectedSubjectId, canCreateSchoolNotes, composeScope]);

    useEffect(() => {
        const onNotesUpdated = () => {
            loadNotes();
        };

        window.addEventListener('notesUpdated', onNotesUpdated);
        return () => window.removeEventListener('notesUpdated', onNotesUpdated);
    }, [loadNotes]);

    const counts = useMemo(() => {
        const result = { open: 0, overdue: 0, important: 0, done: 0, all: notes.length, dueSoon: 0 };
        notes.forEach((note) => {
            const bucket = bucketFor(note);
            if (bucket === 'done') result.done += 1; else result.open += 1;
            if (bucket === 'overdue') result.overdue += 1;
            if (bucket === 'today' || bucket === 'week') result.dueSoon += 1;
            if (note.is_important === 1 && note.is_done !== 1) result.important += 1;
        });
        return result;
    }, [notes]);

    const groups = useMemo(() => {
        const visible = notes.filter((note) => {
            const bucket = bucketFor(note);
            if (view === 'open') return bucket !== 'done';
            if (view === 'overdue') return bucket === 'overdue';
            if (view === 'important') return note.is_important === 1 && bucket !== 'done';
            if (view === 'done') return bucket === 'done';
            return true;
        });

        visible.sort((left, right) => {
            if (left.is_important !== right.is_important) return (right.is_important || 0) - (left.is_important || 0);
            switch (sortBy) {
                case 'due_date':
                    if (!left.due_date && !right.due_date) return new Date(right.created_at) - new Date(left.created_at);
                    if (!left.due_date) return 1;
                    if (!right.due_date) return -1;
                    return new Date(left.due_date) - new Date(right.due_date);
                case 'subject_name':
                    return (left.subject_name || left.scope_label || '').localeCompare(right.subject_name || right.scope_label || '');
                default:
                    return new Date(right.created_at) - new Date(left.created_at);
            }
        });

        return BUCKETS
            .map((bucket) => ({ ...bucket, notes: visible.filter((note) => bucketFor(note) === bucket.id) }))
            .filter((bucket) => bucket.notes.length > 0);
    }, [notes, view, sortBy]);

    const getClassScopeText = (entry) => {
        const classCount = Number(entry?.class_count) || 0;
        if (classCount > 1) {
            return entry?.class_summary || `${classCount} classes`;
        }
        return entry?.class_name || entry?.class_summary || '';
    };

    const formatSubjectOption = (subject) => {
        const parts = [subject.name];
        if (subject.grade !== undefined && subject.grade !== null && subject.grade !== '') {
            parts.push(`Grade ${subject.grade}`);
        }
        const classScope = getClassScopeText(subject);
        if (classScope) {
            parts.push(classScope);
        }
        return parts.join(' · ');
    };

    const getNoteContext = (note) => {
        if (note.note_scope === 'school') {
            return note.creator_name ? `School-wide · ${note.creator_name}` : 'School-wide';
        }

        const parts = [note.subject_name];
        if (note.subject_grade !== undefined && note.subject_grade !== null && note.subject_grade !== '') {
            parts.push(`Gr ${note.subject_grade}`);
        }
        const classScope = getClassScopeText(note);
        if (classScope) {
            parts.push(classScope);
        }
        return parts.filter(Boolean).join(' · ');
    };

    const handleCreateNote = async (event) => {
        event.preventDefault();

        const cleanText = noteText.trim();
        if (!cleanText) return;

        if (composeScope === 'subject' && !selectedSubjectId) {
            alert('Choose a subject first.');
            return;
        }

        if (composeScope === 'school' && !canCreateSchoolNotes) {
            alert('Only admins can create school-wide notes.');
            return;
        }

        setSaving(true);
        try {
            const endpoint = composeScope === 'school' ? 'school_notes.php' : 'subject_notes.php';
            const payload = {
                action: 'create',
                note_text: cleanText,
                is_important: isImportant ? 1 : 0,
                due_date: dueDate || null
            };

            if (composeScope === 'subject') {
                payload.subject_id = selectedSubjectId;
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, payload);
            if (!response.data.success) {
                alert(response.data.error || 'Failed to save note');
                return;
            }

            setNoteText('');
            setDueDate('');
            setIsImportant(false);
            await loadNotes();
            emitNotesUpdated(composeScope === 'subject' ? { subject_id: selectedSubjectId } : { scope: 'school' });
        } catch (requestError) {
            alert(requestError.response?.data?.error || 'Failed to save note');
        } finally {
            setSaving(false);
        }
    };

    const updateNote = async (note, patch) => {
        const endpoint = note.note_scope === 'school' ? 'school_notes.php' : 'subject_notes.php';
        // Show the change straight away; the reload below confirms it.
        setNotes((current) => current.map((item) => (item === note ? { ...item, ...patch } : item)));

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
                action: 'update',
                note_id: note.id,
                ...patch
            });

            if (!response.data.success) {
                alert(response.data.error || 'Failed to update note');
            }

            await loadNotes();
            emitNotesUpdated(note.note_scope === 'subject' ? { subject_id: note.subject_id } : { scope: 'school' });
        } catch (requestError) {
            alert(requestError.response?.data?.error || 'Failed to update note');
            await loadNotes();
        }
    };

    const deleteNote = async (note) => {
        if (!window.confirm('Delete this note?')) return;

        const endpoint = note.note_scope === 'school' ? 'school_notes.php' : 'subject_notes.php';

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
                action: 'delete',
                note_id: note.id
            });

            if (!response.data.success) {
                alert(response.data.error || 'Failed to delete note');
                return;
            }

            await loadNotes();
            emitNotesUpdated(note.note_scope === 'subject' ? { subject_id: note.subject_id } : { scope: 'school' });
        } catch (requestError) {
            alert(requestError.response?.data?.error || 'Failed to delete note');
        }
    };

    if (loading) {
        return <div className="loading_state">Loading notes...</div>;
    }

    const canSaveSubjectNote = composeScope === 'subject' && selectedSubjectId;
    const canSaveSchoolNote = composeScope === 'school' && canCreateSchoolNotes;
    const canSubmit = noteText.trim() !== '' && !saving && (canSaveSubjectNote || canSaveSchoolNote);
    const presets = quickDueDates();
    const viewCount = { open: counts.open, overdue: counts.overdue, important: counts.important, done: counts.done, all: counts.all };
    const emptyCopy = {
        open: ['Nothing to do', 'You’re all caught up. Add a reminder on the left when something comes up.'],
        overdue: ['Nothing overdue', 'Every reminder with a due date is still on time.'],
        important: ['No important reminders', 'Tap the star on a reminder to keep it at the top.'],
        done: ['Nothing completed yet', 'Tick the circle next to a reminder once it’s done.'],
        all: ['No reminders yet', 'Add your first one on the left, like a test date or a parent meeting.'],
    }[view];

    return (
        <div className="nr-page">
            <header className="nr-header">
                <div>
                    <h1>Notes &amp; reminders</h1>
                    <p>
                        Test dates, marking deadlines and meetings, all in one place.
                        {canCreateSchoolNotes ? ' School-wide notices reach every teacher.' : ''}
                    </p>
                </div>
                <dl className="nr-stats">
                    <div className={counts.overdue ? 'is-alert' : ''}><dt>Overdue</dt><dd>{counts.overdue}</dd></div>
                    <div><dt>Due this week</dt><dd>{counts.dueSoon}</dd></div>
                    <div><dt>Done</dt><dd>{counts.done}</dd></div>
                </dl>
            </header>

            {error && <div className="nr-error" role="alert">{error}</div>}

            <div className="nr-layout">
                <form className="nr-composer" onSubmit={handleCreateNote}>
                    <h2>New reminder</h2>

                    {canCreateSchoolNotes && (
                        <div className="nr-segmented" role="group" aria-label="Who sees this note">
                            <button type="button" className={composeScope === 'subject' ? 'is-active' : ''} aria-pressed={composeScope === 'subject'} onClick={() => setComposeScope('subject')}>
                                <Icons.Book /> My subject
                            </button>
                            <button type="button" className={composeScope === 'school' ? 'is-active' : ''} aria-pressed={composeScope === 'school'} onClick={() => setComposeScope('school')}>
                                <Icons.Megaphone /> Whole school
                            </button>
                        </div>
                    )}

                    <label className="nr-label" htmlFor="notes-main-textarea">What do you need to remember?</label>
                    <textarea
                        id="notes-main-textarea"
                        className="nr-textarea"
                        value={noteText}
                        onChange={(event) => setNoteText(event.target.value)}
                        placeholder={composeScope === 'school'
                            ? 'e.g. Staff meeting Thursday 14:00 in the library'
                            : 'e.g. Grade 5 maths test on Friday – remind them to bring calculators'}
                        rows={4}
                    />

                    {composeScope === 'subject' ? (
                        <>
                            <label className="nr-label" htmlFor="note-subject-select">Subject</label>
                            <select
                                id="note-subject-select"
                                className="nr-input"
                                value={selectedSubjectId}
                                onChange={(event) => setSelectedSubjectId(event.target.value)}
                                disabled={subjects.length === 0}
                            >
                                {subjects.length === 0 && <option value="">No subjects yet</option>}
                                {subjects.map((subject) => (
                                    <option key={subject.id} value={subject.id}>{formatSubjectOption(subject)}</option>
                                ))}
                            </select>
                            {subjects.length === 0 && <p className="nr-hint">Add a subject first, then you can link reminders to it.</p>}
                        </>
                    ) : (
                        <p className="nr-hint nr-school-hint">Everyone at your school will see this on their reminders page.</p>
                    )}

                    <label className="nr-label" htmlFor="notes-due-date">Due <span>(optional)</span></label>
                    <div className="nr-presets" role="group" aria-label="Quick due dates">
                        {presets.map((preset) => (
                            <button key={preset.label} type="button" className={dueDate === preset.value ? 'is-active' : ''} aria-pressed={dueDate === preset.value} onClick={() => setDueDate(dueDate === preset.value ? '' : preset.value)}>
                                {preset.label}
                            </button>
                        ))}
                    </div>
                    <input
                        id="notes-due-date"
                        type="datetime-local"
                        className="nr-input"
                        value={dueDate}
                        onChange={(event) => setDueDate(event.target.value)}
                    />

                    <div className="nr-compose-actions">
                        <button type="button" className={`nr-star-toggle${isImportant ? ' is-on' : ''}`} aria-pressed={isImportant} onClick={() => setIsImportant((value) => !value)}>
                            <Icons.Star filled={isImportant} /> Important
                        </button>
                        <button className="nr-add" type="submit" disabled={!canSubmit}>
                            {saving ? 'Saving…' : <><Icons.Plus /> Add reminder</>}
                        </button>
                    </div>
                </form>

                <section className="nr-list-panel" aria-label="Your reminders">
                    <div className="nr-toolbar">
                        <div className="nr-chips" role="tablist" aria-label="Show">
                            {VIEWS.map((item) => (
                                <button key={item.id} type="button" role="tab" aria-selected={view === item.id} className={view === item.id ? 'is-active' : ''} onClick={() => setView(item.id)}>
                                    {item.label}
                                    <span className={item.id === 'overdue' && viewCount.overdue ? 'is-alert' : ''}>{viewCount[item.id]}</span>
                                </button>
                            ))}
                        </div>
                        <label className="nr-sort">
                            <span>Sort</span>
                            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                                <option value="due_date">By due date</option>
                                <option value="created_at">Newest first</option>
                                <option value="subject_name">By subject</option>
                            </select>
                        </label>
                    </div>

                    {groups.length > 0 ? groups.map((group) => (
                        <div key={group.id} className={`nr-group nr-group-${group.id}`}>
                            <h3>{group.title} <span>{group.notes.length}</span></h3>
                            <ul>
                                {group.notes.map((note) => {
                                    const bucket = bucketFor(note);
                                    return (
                                        <li key={`${note.note_scope}-${note.id}`} className={`nr-note is-${bucket}${note.is_important ? ' is-important' : ''}`}>
                                            {note.can_toggle_done ? (
                                                <button
                                                    type="button"
                                                    className="nr-check"
                                                    aria-label={note.is_done ? 'Mark as not done' : 'Mark as done'}
                                                    aria-pressed={Boolean(note.is_done)}
                                                    onClick={() => updateNote(note, { is_done: note.is_done ? 0 : 1 })}
                                                >
                                                    {note.is_done ? <Icons.Check /> : null}
                                                </button>
                                            ) : <span className="nr-check is-static" aria-hidden="true" />}

                                            <div className="nr-note-body">
                                                <p className="nr-note-text">{note.note_text}</p>
                                                <div className="nr-note-meta">
                                                    <span className={`nr-tag ${note.note_scope === 'school' ? 'is-school' : ''}`}>
                                                        {note.note_scope === 'school' ? <Icons.Megaphone /> : <Icons.Book />}
                                                        {getNoteContext(note)}
                                                    </span>
                                                    {note.due_date ? (
                                                        <span className={`nr-due is-${bucket}`}>
                                                            <Icons.Calendar /> {bucket === 'overdue' ? 'Was due ' : ''}{formatDue(note.due_date)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <div className="nr-note-actions">
                                                {note.can_edit ? (
                                                    <button
                                                        type="button"
                                                        className={`nr-icon-btn nr-star${note.is_important ? ' is-on' : ''}`}
                                                        aria-label={note.is_important ? 'Remove from important' : 'Mark as important'}
                                                        aria-pressed={Boolean(note.is_important)}
                                                        onClick={() => updateNote(note, { is_important: note.is_important ? 0 : 1 })}
                                                    >
                                                        <Icons.Star filled={Boolean(note.is_important)} />
                                                    </button>
                                                ) : note.is_important ? <span className="nr-icon-btn nr-star is-on" aria-label="Important"><Icons.Star filled /></span> : null}
                                                {note.can_delete ? (
                                                    <button type="button" className="nr-icon-btn nr-delete" aria-label="Delete reminder" onClick={() => deleteNote(note)}>
                                                        <Icons.Trash />
                                                    </button>
                                                ) : null}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )) : (
                        <div className="nr-empty">
                            <Icons.Note />
                            <h3>{emptyCopy[0]}</h3>
                            <p>{emptyCopy[1]}</p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
